import { ActionIcon, Popover, Slider, Text } from "@mantine/core";
import { Settings2 } from "lucide-react";

export interface ColumnsSettingProps {
  /** The gallery's column count, 2 to 12. */
  value: number;
  onChange: (columns: number) => void;
  /** On the button, for its place in the page's toolbar. */
  className?: string | undefined;
  /**
   * The counts offered, shown as marks under the slider, as live's Sounds page has it (2, 4, 6,
   * 8, 10). Without them the slider runs from 2 to 12 by one, as the library's does.
   */
  marks?: readonly number[] | undefined;
}

/**
 * The live app's display settings of a gallery (design-system.md 3.5, 3.6), the library's and
 * the products': a button that opens "Display settings" with a slider of the column count, named
 * "Columns" for a screen reader, which also reads its value. The page keeps the count in prefs.
 */
export function ColumnsSetting({ value, onChange, className, marks }: ColumnsSettingProps) {
  const first = marks?.[0] ?? 2;
  const last = marks?.at(-1) ?? 12;
  const step = marks !== undefined && marks.length > 1 ? marks[1]! - first : 1;
  return (
    <Popover position="bottom-end" width={260} shadow="md">
      <Popover.Target>
        <ActionIcon
          variant="text"
          size="lg"
          aria-label="Display settings"
          {...(className === undefined ? {} : { className })}
        >
          <Settings2 size={20} strokeWidth={1.5} aria-hidden />
        </ActionIcon>
      </Popover.Target>
      <Popover.Dropdown>
        <Text fw={600} size="sm" mb="sm">
          Display settings
        </Text>
        <Text size="sm" mb={4}>
          Columns
        </Text>
        <Slider
          min={first}
          max={last}
          step={step}
          {...(marks === undefined
            ? {}
            : { marks: marks.map((count) => ({ value: count, label: String(count) })) })}
          mb={marks === undefined ? 0 : "lg"}
          value={value}
          onChange={onChange}
          label={(columns) => `${columns} columns`}
          thumbLabel="Columns"
        />
      </Popover.Dropdown>
    </Popover>
  );
}
